/**
 * Time log archive visibility helpers.
 *
 * Archive state is stored on `is_archived` (preferred) and legacy
 * `approval_status = 'archived'`. Operational SELECT queries must exclude both.
 */

import { loadCachedOrgSettings } from "./orgSettingsBridge";
import {
  endOfPayrollWeek,
  getOffsetPayrollWeekBounds,
  resolvePayrollWeekStartsOn,
  startOfPayrollWeek,
  toLocalDateIso,
} from "./payrollWeek";
import { toLocalDateInputValue } from "./dateTimeHelpers";

export function getTimeLogArchivePayload() {
  return { is_archived: true };
}

export function getTimeLogArchiveFallbackPayload() {
  return { approval_status: "archived" };
}

export function isTimeLogArchived(log) {
  return log?.is_archived === true || log?.approval_status === "archived";
}

/**
 * Restrict a Supabase time_logs query to operational (non-archived) rows.
 * Pass includeArchived: true only when a screen explicitly manages archived records.
 */
export function applyActiveTimelogFilter(query, { includeArchived = false } = {}) {
  if (includeArchived) {
    return query;
  }

  return query.eq("is_archived", false).neq("approval_status", "archived");
}

export function getVisibleTimeLogs(logs = []) {
  return logs.filter((log) => !isTimeLogArchived(log));
}

function formatLogDateForSearch(log) {
  const dateValues = [log?.date_iso];

  if (log?.start_time) {
    dateValues.push(toLocalDateInputValue(log.start_time));
  }

  if (log?.end_time) {
    dateValues.push(toLocalDateInputValue(log.end_time));
  }

  return [...new Set(dateValues.filter(Boolean))]
    .flatMap((dateIso) => {
      const date = new Date(`${dateIso}T12:00:00`);

      return [
        dateIso,
        date.toLocaleDateString("en-CA", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }),
        date.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
        }),
        date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
      ];
    })
    .join(" ");
}

export function timeLogMatchesSearch(log, search) {
  if (!search) {
    return true;
  }

  const dateSearchBlob = formatLogDateForSearch(log);

  return (
    log.employee?.toLowerCase().includes(search) ||
    log.project_name?.toLowerCase().includes(search) ||
    log.client_name?.toLowerCase().includes(search) ||
    log.notes?.toLowerCase().includes(search) ||
    dateSearchBlob.toLowerCase().includes(search)
  );
}

export const TIME_LOG_DATE_RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "custom_range", label: "Custom Range" },
];

function getLogDateIso(log) {
  if (log?.date_iso) {
    return log.date_iso;
  }

  if (log?.start_time) {
    return log.start_time.substring(0, 10);
  }

  return null;
}

/**
 * Resolve a Time Logs / Payroll date-range bucket to inclusive ISO bounds.
 * Returns null start/end for `custom_range` (caller supplies dates) or unknown keys.
 */
export function resolveTimeLogDateRangeBounds(rangeKey, options = {}) {
  if (!rangeKey || rangeKey === "custom_range") {
    return { startDate: null, endDate: null };
  }

  const reference = options.referenceDate
    ? new Date(options.referenceDate)
    : new Date();
  reference.setHours(12, 0, 0, 0);

  const weekStartsOn = resolvePayrollWeekStartsOn(
    options.orgSettings ?? loadCachedOrgSettings(),
  );

  switch (rangeKey) {
    case "today": {
      const iso = toLocalDateIso(reference);
      return { startDate: iso, endDate: iso };
    }
    case "yesterday": {
      const yesterday = new Date(reference);
      yesterday.setDate(yesterday.getDate() - 1);
      const iso = toLocalDateIso(yesterday);
      return { startDate: iso, endDate: iso };
    }
    case "this_week": {
      const weekStart = startOfPayrollWeek(reference, weekStartsOn);
      const weekEnd = endOfPayrollWeek(reference, weekStartsOn);
      return {
        startDate: toLocalDateIso(weekStart),
        endDate: toLocalDateIso(weekEnd),
      };
    }
    case "last_week": {
      const { weekStart, weekEnd } = getOffsetPayrollWeekBounds(
        reference,
        weekStartsOn,
        -1,
      );
      return {
        startDate: toLocalDateIso(weekStart),
        endDate: toLocalDateIso(weekEnd),
      };
    }
    case "this_month": {
      const start = new Date(reference.getFullYear(), reference.getMonth(), 1);
      const end = new Date(reference.getFullYear(), reference.getMonth() + 1, 0);
      start.setHours(12, 0, 0, 0);
      end.setHours(12, 0, 0, 0);
      return {
        startDate: toLocalDateIso(start),
        endDate: toLocalDateIso(end),
      };
    }
    case "last_month": {
      const start = new Date(reference.getFullYear(), reference.getMonth() - 1, 1);
      const end = new Date(reference.getFullYear(), reference.getMonth(), 0);
      start.setHours(12, 0, 0, 0);
      end.setHours(12, 0, 0, 0);
      return {
        startDate: toLocalDateIso(start),
        endDate: toLocalDateIso(end),
      };
    }
    default:
      return { startDate: null, endDate: null };
  }
}

export function timeLogMatchesProjectFilter(log, projectId) {
  if (!projectId) {
    return true;
  }

  return String(log?.project_id || "") === String(projectId);
}

export function timeLogMatchesDateRange(log, rangeKey, options = {}) {
  if (!rangeKey || rangeKey === "custom_range") {
    return true;
  }

  const logDateIso = getLogDateIso(log);

  if (!logDateIso) {
    return false;
  }

  const { startDate, endDate } = resolveTimeLogDateRangeBounds(rangeKey, options);

  if (!startDate || !endDate) {
    return true;
  }

  return logDateIso >= startDate && logDateIso <= endDate;
}

export function filterTimeLogs(
  logs,
  {
    search = "",
    projectId = "",
    dateRange = "this_month",
    orgSettings,
  } = {},
) {
  const normalizedSearch = search.toLowerCase().trim();
  const dateRangeOptions = orgSettings ? { orgSettings } : {};

  return getVisibleTimeLogs(logs).filter(
    (log) =>
      timeLogMatchesSearch(log, normalizedSearch) &&
      timeLogMatchesProjectFilter(log, projectId) &&
      timeLogMatchesDateRange(log, dateRange, dateRangeOptions),
  );
}

export async function archiveTimeLog(supabaseClient, logId) {
  let { error } = await supabaseClient
    .from("time_logs")
    .update(getTimeLogArchivePayload())
    .eq("id", logId);

  if (
    error &&
    /is_archived|column/i.test(error.message || "")
  ) {
    ({ error } = await supabaseClient
      .from("time_logs")
      .update(getTimeLogArchiveFallbackPayload())
      .eq("id", logId));
  }

  return { error };
}

export async function deleteTimeLog(supabaseClient, logId) {
  const { error } = await supabaseClient
    .from("time_logs")
    .delete()
    .eq("id", logId);

  return { error };
}

export function groupTimeLogsByEmployee(logs = []) {
  const groups = new Map();

  logs.forEach((log) => {
    const employeeName = log.employee || "Unknown Employee";

    if (!groups.has(employeeName)) {
      groups.set(employeeName, []);
    }

    groups.get(employeeName).push(log);
  });

  return Array.from(groups.entries()).sort(([nameA], [nameB]) =>
    nameA.localeCompare(nameB),
  );
}
